const USER_AGENT = window.navigator.userAgent;

export function isWebRTCAPISupported() {
  const arr = ['RTCPeerConnection', 'webkitRTCPeerConnection', 'mozRTCPeerConnection', 'RTCIceGatherer'];

  for (let i = 0; i < arr.length; i++) {
    if (arr[i] in window) {
      return true;
    }
  }

  return false;
}

export const IS_FIREFOX = /firefox\/(\d+)\./i.test(USER_AGENT);
export const IS_SAFARI = /safari\/(\d+)\./i.test(USER_AGENT) && !/chrome\/(\d+)\./i.test(USER_AGENT);
export const IS_IOS = /iPad|iPhone|iPod/i.test(USER_AGENT);
export const IOS_VERSION_ARR = function() {
  const matches = USER_AGENT.match(/OS (\d+)_(\d+)_?(\d+)?/i);
  return matches ? [parseInt(matches[1], 10), parseInt(matches[2], 10), parseInt(matches[3] || '0', 10)] : [];
}();

export const uuid = (len = 21) => crypto.getRandomValues(new Uint8Array(len)).reduce(((e, t) => e += (t &= 63) < 36 ? t.toString(36) : t < 62 ? (t - 26).toString(36).toUpperCase() : t > 62 ? '-' : '_'), '');

declare global {
  interface RTCStatsReport {
    get(id: string): any;
  }
}

export const formatStatsReport = (stats: RTCStatsReport, lastStats: RTCStatsReport = null) => {
  if (!lastStats) return;

  let videoTrackReportId = null;
  let videoRtpReportId = null;
  let audioTrackReportId = null;
  let audioRtpReportId = null;

  stats.forEach(it => {
    if (it.type === 'track') {
      if (it.kind === 'video' || it.frameWidth) {
        videoTrackReportId = it.id;
      } else if (it.kind === 'audio' || it.audioLevel) {
        audioTrackReportId = it.id;
      }
    }
    if (it.type === 'inbound-rtp') {
      if (it.kind === 'video' || it.mediaType === 'video') {
        videoRtpReportId = it.id;
      } else if (it.kind === 'audio' || it.mediaType === 'audio') {
        audioRtpReportId = it.id;
      }
    }
  });

  const videoTrackReport = stats.get(videoTrackReportId);
  const videoRtpReport = stats.get(videoRtpReportId);
  const lastVideoTrackReport = lastStats.get(videoTrackReportId);
  const lastVideoRtpReport = lastStats.get(videoRtpReportId);

  let duration = 0;
  if (undefined !== videoRtpReport?.timestamp && undefined !== lastVideoRtpReport?.timestamp) {
    duration = (videoRtpReport.timestamp - lastVideoRtpReport.timestamp) / 1000;
  }

  let videoCodec = undefined;
  if (videoRtpReport?.codecId) {
    const codecReport = stats.get(videoRtpReport.codecId);
    if (codecReport) {
      const { mimeType, payloadType, sdpFmtpLine } = codecReport;
      videoCodec = mimeType?.replace('video/', '') || '';
      if (payloadType && sdpFmtpLine) {
        videoCodec = videoCodec + ' (' + payloadType + ', ' + sdpFmtpLine + ')';
      }
    }
  }

  let framesPerSecond = undefined;
  if (undefined !== videoRtpReport?.framesPerSecond) {
    framesPerSecond = videoRtpReport.framesPerSecond;
  } else if (undefined !== videoRtpReport?.framerateMean) {
    framesPerSecond = videoRtpReport.framerateMean;
  } else if (undefined !== videoTrackReport?.framesReceived && undefined !== lastVideoTrackReport?.framesReceived && duration) {
    framesPerSecond = (videoTrackReport.framesReceived - lastVideoTrackReport.framesReceived) / duration;
  }

  let framesDecodedPerSecond = undefined;
  if (undefined !== videoRtpReport?.framesDecoded && undefined !== lastVideoRtpReport?.framesDecoded && duration) {
    framesDecodedPerSecond = (videoRtpReport.framesDecoded - lastVideoRtpReport.framesDecoded) / duration;
  }

  let videoBitrate = undefined;
  if (undefined !== videoRtpReport?.bytesReceived && undefined !== lastVideoRtpReport?.bytesReceived && duration) {
    videoBitrate = 8 * (videoRtpReport.bytesReceived - lastVideoRtpReport.bytesReceived) / duration;
  }

  let videoJitter = undefined;
  if (undefined !== videoTrackReport?.jitterBufferDelay && undefined !== videoTrackReport?.jitterBufferEmittedCount && undefined !== lastVideoTrackReport?.jitterBufferDelay && undefined !== lastVideoTrackReport?.jitterBufferEmittedCount) {
    videoJitter = videoTrackReport.jitterBufferEmittedCount - lastVideoTrackReport.jitterBufferEmittedCount ? (videoTrackReport.jitterBufferDelay - lastVideoTrackReport.jitterBufferDelay) / (videoTrackReport.jitterBufferEmittedCount - lastVideoTrackReport.jitterBufferEmittedCount) * 1e3 : videoTrackReport.jitterBufferDelay / videoTrackReport.jitterBufferEmittedCount * 1e3;
  }

  let frameDecodeAvgTime = undefined;
  if (undefined !== videoRtpReport?.totalDecodeTime && undefined !== videoTrackReport?.framesDecoded && undefined !== lastVideoRtpReport?.totalDecodeTime && undefined !== lastVideoTrackReport?.framesDecoded) {
    frameDecodeAvgTime = videoTrackReport.framesDecoded - lastVideoTrackReport.framesDecoded ? (videoRtpReport.totalDecodeTime - lastVideoRtpReport.totalDecodeTime) / (videoTrackReport.framesDecoded - lastVideoTrackReport.framesDecoded) * 1e3 : videoRtpReport.totalDecodeTime / videoTrackReport.framesDecoded * 1e3;
  }

  let audioTrackReport = stats.get(audioTrackReportId);
  let audioRtpReport = stats.get(audioRtpReportId);
  let lastAudioTrackReport = lastStats.get(audioTrackReportId);
  let lastAudioRtpReport = lastStats.get(audioRtpReportId);

  if (undefined !== audioRtpReport?.timestamp && undefined !== lastAudioRtpReport?.timestamp) {
    duration = (audioRtpReport.timestamp - lastAudioRtpReport.timestamp) / 1000;
  }

  let audioCodec = undefined;
  if (audioRtpReport?.codecId) {
    const codecReport = stats.get(audioRtpReport.codecId);
    if (codecReport) {
      const { mimeType, payloadType, sdpFmtpLine } = codecReport;
      audioCodec = mimeType?.replace('audio/', '') || '';
      if (payloadType && sdpFmtpLine) {
        audioCodec = audioCodec + ' (' + payloadType + ', ' + sdpFmtpLine + ')';
      }
    }
  }

  let audioLevel = undefined;
  if (undefined !== audioTrackReport?.audioLevel) {
    audioLevel = audioTrackReport?.audioLevel;
  } else if (undefined !== audioRtpReport?.audioLevel) {
    audioLevel = audioRtpReport?.audioLevel;
  }

  let audioBitrate = undefined;
  if (undefined !== audioRtpReport?.bytesReceived && undefined !== lastAudioRtpReport?.bytesReceived && duration) {
    audioBitrate = 8 * (audioRtpReport.bytesReceived - lastAudioRtpReport.bytesReceived) / duration;
  }

  let audioJitter = undefined;
  if (undefined !== audioTrackReport?.jitterBufferDelay && undefined !== audioTrackReport?.jitterBufferEmittedCount && undefined !== lastAudioTrackReport?.jitterBufferDelay && undefined !== lastAudioTrackReport?.jitterBufferEmittedCount) {
    audioJitter = audioTrackReport.jitterBufferEmittedCount - lastAudioTrackReport.jitterBufferEmittedCount ? (audioTrackReport.jitterBufferDelay - lastAudioTrackReport.jitterBufferDelay) / (audioTrackReport.jitterBufferEmittedCount - lastAudioTrackReport.jitterBufferEmittedCount) * 1e3 : audioTrackReport.jitterBufferDelay / audioTrackReport.jitterBufferEmittedCount * 1e3;
  }

  return {
    timestamp: (videoRtpReport?.timestamp) || (audioRtpReport?.timestamp),
    video: {
      codec: videoCodec,
      bitrate: videoBitrate && Number(videoBitrate.toFixed(2)),
      framesPerSecond: framesPerSecond && Math.round(framesPerSecond),
      framesDecodedPerSecond: framesDecodedPerSecond && Math.round(framesDecodedPerSecond),
      frameWidth: videoTrackReport?.frameWidth,
      frameHeight: videoTrackReport?.frameHeight,
      framesDecoded: videoRtpReport?.framesDecoded,
      framesDropped: videoTrackReport?.framesDropped,
      framesReceived: videoTrackReport?.framesReceived,
      packetsLost: videoRtpReport?.packetsLost,
      packetsReceived: videoRtpReport?.packetsReceived,
      nackCount: videoRtpReport?.nackCount,
      firCount: videoRtpReport?.firCount,
      pliCount: videoRtpReport?.pliCount,
      jitterBufferDelay: videoJitter && Number(videoJitter.toFixed(2)),
      frameDecodeAvgTime: frameDecodeAvgTime && Number(frameDecodeAvgTime.toFixed(2)),
    },
    audio: {
      codec: audioCodec,
      audioLevel: audioLevel,
      bitrate: audioBitrate && Number(audioBitrate.toFixed(2)),
      packetsLost: audioRtpReport?.packetsLost,
      packetsReceived: audioRtpReport?.packetsReceived,
      jitterBufferDelay: audioJitter && Number(audioJitter.toFixed(2)),
    },
  };
};

export const formatDate = (date: Date, format: string) => {
    const o = {
      'M+': date.getMonth() + 1, // 月份
      'd+': date.getDate(), // 日
      'h+': date.getHours() % 12 === 0 ? 12 : date.getHours() % 12, // 小时
      'H+': date.getHours(), // 小时
      'm+': date.getMinutes(), // 分
      's+': date.getSeconds(), // 秒
      'q+': Math.floor((date.getMonth() + 3) / 3), // 季度
      S: date.getMilliseconds(), // 毫秒
      a: date.getHours() < 12 ? '上午' : '下午', // 上午/下午
      A: date.getHours() < 12 ? 'AM' : 'PM', // AM/PM
    };
    if (/(y+)/.test(format)) {
      format = format.replace(RegExp.$1, (date.getFullYear() + '').substr(4 - RegExp.$1.length));
    }
    for (let k in o) {
      if (new RegExp('(' + k + ')').test(format)) {
        format = format.replace(
          RegExp.$1,
          RegExp.$1.length === 1 ? o[k] : ('00' + o[k]).substr(('' + o[k]).length)
        );
      }
    }
    return format;
}
